<template>
  <EditDrawer
    :value="localOpen"
    :persistent="isDirty && !isApplying"
    with-header
    :title="title"
    free-mode
    @input="onDrawerInput"
  >
    <div class="drawer-body">
      <div class="v3-table-wrapper">
        <table class="intervals-table">
          <thead>
            <tr>
              <th></th>
              <th>Start Date</th>
              <th>End Date</th>
              <th
                v-for="metric in metricFields"
                :key="metric.key"
                :class="{ 'column-error': errorColumnKeys.has(metric.key) }"
              >
                {{ metric.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(interval, idx) in channelIntervals" :key="idx">
              <td class="flight-cell">Flight {{ idx + 1 }}</td>
              <td>
                <SingleDatePicker
                  :value="interval.start_date"
                  empty-text="Start Date"
                  :max="interval.end_date || scenarioEndDate"
                  :min="scenarioStartDate"
                  class="app-text-field app-input-bordered table-input"
                  @input="onDateInput(idx, 'start_date', $event)"
                />
              </td>
              <td>
                <SingleDatePicker
                  :value="interval.end_date"
                  empty-text="End Date"
                  :min="interval.start_date || scenarioStartDate"
                  :max="scenarioEndDate"
                  class="app-text-field app-input-bordered table-input"
                  @input="onDateInput(idx, 'end_date', $event)"
                />
              </td>
              <td v-for="metric in metricFields" :key="metric.key">
                <FormattedCurrencyInput
                  v-if="isCurrencyMetric(metric.key) && isEditableMetric(metric.key)"
                  :class="{ 'input-error': hasBudgetOverflow(interval) || errorColumnKeys.has(metric.key) }"
                  :value="Number(intervalMetricValue(interval, metric.key))"
                  :label="metric.label"
                  :title="metric.label"
                  :maximum-fraction-digits="metric.key === 'budget' ? 0 : 2"
                  :disabled="!isEditableMetric(metric.key)"
                  class="app-text-field app-input-bordered table-input"
                  @input="onCurrencyMetricInput(idx, metric.key, $event)"
                />
                <FormattedPercentageInput
                  v-else-if="isPercentageMetric(metric.key) && isEditableMetric(metric.key)"
                  :class="{ 'input-error': hasPercentOverflow(interval) || errorColumnKeys.has(metric.key) }"
                  :value="Number(intervalMetricValue(interval, metric.key))"
                  :label="metric.label"
                  :title="metric.label"
                  :disabled="!isEditableMetric(metric.key)"
                  class="app-text-field app-input-bordered table-input"
                  @input="onPercentageMetricInput(idx, metric.key, $event)"
                />
                <v-text-field
                  v-else
                  :value="displayMetricValue(interval, metric)"
                  :label="metric.label"
                  :title="metric.label"
                  :disabled="!isEditableMetric(metric.key)"
                  hide-details
                  inputmode="decimal"
                  class="app-text-field app-input-bordered table-input"
                  @input="onTextMetricInput(idx, metric.key, $event)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="drawer-actions">
        <ButtonSecondary initial :disabled="isApplyDisabled" @click="onApply">Apply</ButtonSecondary>
      </div>
    </div>
  </EditDrawer>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";

import ButtonSecondary from "@/components/customComponents/ButtonSecondary.vue";
import EditDrawer from "@/components/drawers/EditDrawer.vue";
import SingleDatePicker from "@/components/inputs/DatePicker.vue";
import FormattedCurrencyInput from "@/components/inputs/FormattedCurrencyInput.vue";
import FormattedPercentageInput from "@/components/inputs/FormattedPercentageInput.vue";
import Lang from "@/lang";
import { PlanInputDictionary } from "@/types/common";
import { ProcessedRow } from "@/types/tables";

interface ChannelInterval {
  id: string;
  start_date: string;
  end_date: string;
  budget: string;
  channel_percentage: string;
  [key: string]: string;
}

export type DrawerFlightSave = {
  id: string;
  flight_budget: number;
  flight_percentage: number;
  start_date: string;
  end_date: string;
  [key: string]: string | number;
};

const BASE_DRAWER_FIELD_KEYS = new Set(["channel", "campaign_kpi", "start_date", "end_date"]);
const CURRENCY_DRAWER_FIELD_KEYS = new Set([
  "budget",
  "cpm",
  "cpb",
  "cpc",
  "cpcv",
  "cpe",
  "cper",
  "cpa",
  "cpae",
  "cpi",
  "cpia",
  "cpl",
  "cpla",
  "cplc",
  "cplv",
  "cpp",
  "cppl",
  "cps",
  "cpsa",
  "cpsv",
]);
const PERCENTAGE_DRAWER_FIELD_KEYS = new Set(["channel_percentage", "ctr", "vcr"]);

const PCT_TOLERANCE = 0.01;
const BUDGET_TOLERANCE = 1;
const round2 = (n: number) => Math.round(n * 100) / 100;
const toInt = (n: number) => Math.trunc(n);

function intervalsFromRow(row: ProcessedRow | null): ChannelInterval[] {
  const flights = (row as Record<string, unknown> | null)?.flights;
  if (Array.isArray(flights) && flights.length) {
    return flights.map((f: Record<string, unknown>) => ({ ...f } as ChannelInterval));
  }
  return [];
}

export default Vue.extend({
  name: "ChannelIntervalsDrawer",
  components: { EditDrawer, FormattedCurrencyInput, FormattedPercentageInput, ButtonSecondary, SingleDatePicker },

  props: {
    value: Boolean,
    row: {
      type: Object as PropType<ProcessedRow | null>,
      default: null,
    },
    baseBudget: {
      type: Number,
      default: 0,
    },
    headers: {
      type: Array as PropType<PlanInputDictionary[]>,
      default: () => [],
    },
  },

  data() {
    return {
      Lang,
      localOpen: false,
      isApplying: false,
      channelIntervals: [] as ChannelInterval[],
      initialIntervalsSnapshot: "",
    };
  },

  computed: {
    title(): string {
      return this.row?.channel ? `Edit Channel: ${this.row.channel}` : "Edit Channel";
    },
    totalBudget(): number {
      return Number(this.baseBudget ?? 0);
    },
    metricFields(): Array<{ key: string; label: string; formatter?: PlanInputDictionary["formatter"] }> {
      return Array.isArray(this.headers)
        ? this.headers
            .filter(
              (header) =>
                header.lvl === 2 &&
                !BASE_DRAWER_FIELD_KEYS.has(String(header.value)) &&
                String(header.value) !== "geography" &&
                String(header.value) !== "campaign_id" &&
                String(header.value) !== "business_goal" &&
                String(header.value) !== "plan"
            )
            .map((header) => ({
              key: String(header.value),
              label: String(header.title ?? header.value),
              formatter: header.formatter,
            }))
        : [];
    },
    editableMetricKeys(): Set<string> {
      return new Set(
        (Array.isArray(this.headers) ? this.headers : [])
          .filter((header) => header.editable)
          .map((header) => String(header.value))
      );
    },
    isDirty(): boolean {
      return this.serializeIntervals(this.channelIntervals) !== this.initialIntervalsSnapshot;
    },
    totalPercent(): number {
      return this.channelIntervals.reduce((sum, interval) => sum + (Number(interval.channel_percentage || 0) || 0), 0);
    },
    totalFlightBudget(): number {
      return this.channelIntervals.reduce((sum, interval) => sum + toInt(Number(interval.budget || 0) || 0), 0);
    },
    hasIntervalErrors(): boolean {
      const percentOverflow = this.totalPercent - 100 > PCT_TOLERANCE;
      const budgetOverflow = this.totalFlightBudget - this.totalBudget > BUDGET_TOLERANCE;
      const singleRowOverflow = this.channelIntervals.some(
        (interval) => Number(interval.channel_percentage || 0) > 100 || Number(interval.budget || 0) > this.totalBudget
      );
      return percentOverflow || budgetOverflow || singleRowOverflow;
    },
    isApplyDisabled(): boolean {
      return !this.isDirty || this.hasIntervalErrors;
    },
    errorColumnKeys(): Set<string> {
      const keys = new Set<string>();
      if (this.hasIntervalErrors) {
        keys.add("budget");
        keys.add("channel_percentage");
      }
      return keys;
    },
    scenarioStartDate(): string {
      return String(this.row?.start_date ?? "");
    },
    scenarioEndDate(): string {
      return String(this.row?.end_date ?? "");
    },
  },

  methods: {
    serializeIntervals(intervals: ChannelInterval[]): string {
      return JSON.stringify(
        intervals.map((interval) => ({
          id: String(interval.id ?? ""),
          start_date: String(interval.start_date ?? ""),
          end_date: String(interval.end_date ?? ""),
          budget: String(interval.budget ?? ""),
          channel_percentage: String(interval.channel_percentage ?? ""),
          ...Object.keys(interval)
            .filter((key) => !["id", "start_date", "end_date", "budget", "channel_percentage"].includes(key))
            .sort()
            .reduce<Record<string, string>>((acc, key) => {
              acc[key] = String(interval[key] ?? "");
              return acc;
            }, {}),
        }))
      );
    },
    hasPercentOverflow(interval: ChannelInterval): boolean {
      return Number(interval.channel_percentage || 0) > 100;
    },
    hasBudgetOverflow(interval: ChannelInterval): boolean {
      return Number(interval.budget || 0) > this.totalBudget;
    },
    isCurrencyMetric(key: string): boolean {
      return CURRENCY_DRAWER_FIELD_KEYS.has(key);
    },
    isPercentageMetric(key: string): boolean {
      return PERCENTAGE_DRAWER_FIELD_KEYS.has(key);
    },
    isEditableMetric(key: string): boolean {
      return this.editableMetricKeys.has(key);
    },
    intervalMetricValue(interval: ChannelInterval, key: string): string {
      if (key === "budget") return String(interval.budget ?? "0");
      if (key === "channel_percentage") return String(interval.channel_percentage ?? "0");
      return String(interval[key] ?? "0");
    },
    displayMetricValue(
      interval: ChannelInterval,
      metric: { key: string; label: string; formatter?: PlanInputDictionary["formatter"] }
    ): string {
      const rawValue = this.intervalMetricValue(interval, metric.key);
      if (this.isEditableMetric(metric.key) || !metric.formatter) {
        return rawValue;
      }

      return String(metric.formatter(rawValue));
    },
    onBudgetInput(idx: number, val: number) {
      const interval = { ...this.channelIntervals[idx] };
      const budget = Math.max(0, toInt(val));
      interval.budget = String(budget);
      interval.channel_percentage = String(this.totalBudget > 0 ? round2((budget / this.totalBudget) * 100) : 0);
      this.$set(this.channelIntervals, idx, interval);
    },
    onPctInput(idx: number, val: number) {
      const interval = { ...this.channelIntervals[idx] };
      const pct = Math.max(0, Math.min(100, round2(val)));
      interval.channel_percentage = String(pct);
      interval.budget = String(this.totalBudget > 0 ? toInt((pct / 100) * this.totalBudget) : 0);
      this.$set(this.channelIntervals, idx, interval);
    },
    onCurrencyMetricInput(idx: number, key: string, val: number) {
      if (key === "budget") {
        this.onBudgetInput(idx, val);
        return;
      }

      const interval = { ...this.channelIntervals[idx] };
      interval[key] = String(Number.isFinite(val) ? val : 0);
      this.$set(this.channelIntervals, idx, interval);
    },
    onPercentageMetricInput(idx: number, key: string, val: number) {
      if (key === "channel_percentage") {
        this.onPctInput(idx, val);
        return;
      }

      const interval = { ...this.channelIntervals[idx] };
      interval[key] = String(Number.isFinite(val) ? round2(val) : 0);
      this.$set(this.channelIntervals, idx, interval);
    },
    onTextMetricInput(idx: number, key: string, val: string) {
      if (!this.isEditableMetric(key)) return;

      const interval = { ...this.channelIntervals[idx] };
      interval[key] = String(val ?? "");
      this.$set(this.channelIntervals, idx, interval);
    },
    onDateInput(idx: number, key: "start_date" | "end_date", val: string) {
      const interval = { ...this.channelIntervals[idx] };
      interval[key] = String(val ?? "");
      this.$set(this.channelIntervals, idx, interval);
    },
    async onDrawerInput(val: boolean) {
      if (val) {
        this.localOpen = true;
        this.$emit("input", true);
        return;
      }

      if (!this.isApplying && this.isDirty) {
        const confirmed = await this.$store.dispatch("confirmDialog/open", {
          title: Lang.defaultCloseTitle,
          message: Lang.defaultCloseMessage,
          cancelText: Lang.cancel,
          confirmText: Lang.discard,
        });
        if (!confirmed) {
          this.localOpen = true;
          return;
        }
      }

      this.localOpen = false;
      this.$emit("input", false);
    },
    onApply() {
      const flights: DrawerFlightSave[] = this.channelIntervals.map((f) => {
        const nextFlight: DrawerFlightSave = {
          id: f.id,
          flight_budget: toInt(Number(f.budget)),
          flight_percentage: round2(Number(f.channel_percentage)),
          start_date: f.start_date,
          end_date: f.end_date,
        };

        this.metricFields.forEach((metric) => {
          if (metric.key === "budget" || metric.key === "channel_percentage") return;
          nextFlight[metric.key] = String(f[metric.key] ?? "");
        });

        return nextFlight;
      });
      this.isApplying = true;
      this.initialIntervalsSnapshot = this.serializeIntervals(this.channelIntervals);
      this.$emit("save", flights);
      this.localOpen = false;
      this.$emit("input", false);
      this.$nextTick(() => {
        this.isApplying = false;
      });
    },
  },

  watch: {
    value(val: boolean) {
      this.localOpen = val;
      if (val) {
        this.channelIntervals = intervalsFromRow(this.row);
        this.initialIntervalsSnapshot = this.serializeIntervals(this.channelIntervals);
        this.isApplying = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.v3-table-wrapper {
  overflow-x: auto;
}

.intervals-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  white-space: nowrap;

  th {
    text-align: left;
    font-weight: 600;
    padding: 8px 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.6);

    &.column-error {
      color: var(--v-error-base, #ff5252);
    }
  }

  td {
    padding: 6px 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    vertical-align: middle;
  }

  .flight-cell {
    font-weight: 600;
    color: rgba(255, 255, 255, 0.85);
    padding-right: 16px;
  }

  .table-input {
    min-width: 100px;
  }
}

.drawer-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.input-error {
  ::v-deep .v-input__slot {
    outline: 2px solid var(--v-error-base, #ff5252) !important;
  }

  ::v-deep .v-label {
    color: var(--v-error-base, #ff5252) !important;
  }
}
</style>
